
import { Component, Mixins } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import Matomo from 'matomo-ts';

import {
  FiberMapMixin,
  AuthorizationMixin,
  SaveScrollMixin,
  TextToSpeechMixin,
  StyleMixin
} from '@/@next/mixins';
import {
  PointsOfInterestService,
  CollectionsService,
  DatasetService,
  MediasService
} from '@/@next/services';
import { SvgIcon, IconTextToSpeech } from '@/@next/components/icons';
import { messages } from './i18n';

import { MAP_URL, MATOMO_URL } from '@/constants';
import {
  Maybe,
  PointOfInterest,
  PointOfInterestList,
  Collection,
  Person,
  AudioGuideTrack,
  MediaImage,
  Content,
  PointOfInterestContentData,
  CollectionContentData,
  PersonContentData,
  QuestionContentData,
  QuizContentData,
  SurveyContentData,
  HtmlTranslation
} from '@/@types/graphql';

import * as UI from '@/event-bus';

import * as AUDIOGUIDE from '@/@next/store/audioguide/types';
import * as BOT from '@/@next/store/bot/types';
import * as SETTINGS from '@/@next/store/settings2/types';

import { CAsyncTask } from '@/@next/components/async-task';
import CustomWrapper from '@/components/CustomWrapper.vue';
import { CImage } from '@/@next/components/image';
import { CLoader } from '@/components/loader';
import { CAudioGuideButton, CAudioGuideCount } from '@/components/audioguide';
import { CTopbarTitle } from '@/@next/components/topbar-title';
import CModal from '@/components/modal';
import VSinglePoiContentPuzzle from '@/views/single-poi/contents/puzzle/Puzzle.vue';
import CAuthorModal from '@/components/modal/author-modal';
import { CPoiContent } from '@/@next/components/poi-content';
import format from 'date-fns/format';
import * as LANGUAGES from '@/@next/store/languages/types';
import { CButton } from '@/components/form-controls/button';
import { smallerImageUrl } from '@/@next/helpers';

const config = {
  name: 'v-single-poi',
  components: {
    CAsyncTask,
    CPoiContent,
    ...CModal,
    ...CAuthorModal,
    CustomWrapper,
    CAudioGuideButton,
    CAudioGuideCount,
    CLoader,
    CImage,
    VSinglePoiContentPuzzle,
    CTopbarTitle,
    CButton,
    SvgIcon,
    IconTextToSpeech
  },
  i18n: { messages },
  head () {
    return {
      title: this.tabTitle,
      titleTemplate: '%s – Wivi'
    };
  }
};

@Component
class PoiServices extends Mixins(
  PointsOfInterestService,
  CollectionsService,
  DatasetService,
  MediasService
) {}

@Component
class PoiMixins extends Mixins(
  SaveScrollMixin,
  FiberMapMixin,
  AuthorizationMixin,
  TextToSpeechMixin,
  StyleMixin
) {}

@Component(config)
export default class SinglePoi extends Mixins!(PoiServices, PoiMixins) {
  @Getter(AUDIOGUIDE.GET_STATUS) audioguideStatus!: boolean;
  @Action(AUDIOGUIDE.SET_STATUS) setAudioguideStatus!: any;
  @Action(AUDIOGUIDE.SET_PLAYLIST) setAudioguidePlaylist!: any;
  @Action(AUDIOGUIDE.SET_POI) setAudioguidePoi!: any;
  @Getter(AUDIOGUIDE.GET_POI) getAudioguidePoi!: any;
  @Action(AUDIOGUIDE.SET_CURRENT_POI) setAudioguideCurrentPoi!: (
    poi: any
  ) => Promise<any>;
  @Action(BOT.ADD_POI_CONTENT) addPoiContent!: any;
  @Getter(BOT.IS_ENABLE) isBotEnable!: any;
  @Getter(BOT.IS_OPEN) isBotOpen!: boolean;
  @Action(BOT.CLOSING_BOT) closeBot!: any;
  // @Getter(SETTINGS.GET_ALL) getAllSettings!: any
  @Getter(LANGUAGES.GET_CURRENT) getCurrentLanguage!: string;
  @Getter(SETTINGS.GET_FEATURES) _settingsFeatures!: any;
  @Getter(SETTINGS.GET_SETTINGS_CONFIGURATION) _getSettingsConfiguration!: any;
  // @Action(FIBERMAP.CONTENT_HAS_LINK)
  // isPresentOnTheMap!: ({type, uuid}: {type: string, uuid: string}) => boolean

  pointOfInterest: Maybe<PointOfInterest> = null;
  pointsOfInterest: Maybe<PointOfInterestList> = null;
  collection: Maybe<Collection> = null;
  showPersonModal: boolean = false;
  scrollPosY: number = 0;
  mediaScale: number = 1;
  person: Person | null = null;
  speechSynthesisIsActive: boolean = false;
  speechSynthesisIsPlayed: boolean = false;
  speechSynthesisIsReady: boolean = false;
  mediaSyncContent: any | null = null;
  // displayMapLink: boolean = false
  smallerImageUrl = smallerImageUrl;

  get autoStartAudioGuideWhenPoiIsOpened () {
    return (
      this._getSettingsConfiguration?.audioGuide
        ?.autoStartAudioGuideWhenPoiIsOpened || false
    );
  }

  get autoStopAudioGuideWhenDifferentPoiIsOpened () {
    return (
      this._getSettingsConfiguration?.audioGuide
        ?.autoStopAudioGuideWhenDifferentPoiIsOpened || false
    );
  }

  get autoStopAudioGuideWhenPoiIsClosed () {
    return (
      this._getSettingsConfiguration?.audioGuide
        ?.autoStopAudioGuideWhenPoiIsClosed || false
    );
  }

  get contents () {
    const ressourceLanguageFilter = (c: Content) => {
      switch (c.__typename) {
        case 'PointOfInterestContent': {
          const poiData = c.data as PointOfInterestContentData;
          return poiData.pointOfInterest?.languages.includes(this.$i18n.locale);
        }
        case 'CollectionContent': {
          const collectionData = c.data as CollectionContentData;
          return collectionData.collection?.languages.includes(this.$i18n.locale);
        }
        case 'PersonContent': {
          const personData = c.data as PersonContentData;
          return personData.person?.languages.includes(this.$i18n.locale);
        }
        case 'QuestionContent': {
          const questionData = c.data as QuestionContentData;
          return questionData.question?.languages.includes(this.$i18n.locale);
        }
        case 'QuizContent': {
          const quizData = c.data as QuizContentData;
          return quizData.quiz?.languages.includes(this.$i18n.locale);
        }
        case 'SurveyContent': {
          const surveyData = c.data as SurveyContentData;
          return surveyData.survey?.languages.includes(this.$i18n.locale);
        }
        default:
          return true;
      }
    };
    const content = this.pointOfInterest?.contents.filter(
      ressourceLanguageFilter
    );
    return content || [];
  }

  async fetchNextPoi (currentId: PointOfInterest, list: PointOfInterest[]) {
    const index = list.findIndex((p: PointOfInterest) => p.id === currentId);
    const nextId = index < list.length - 1 && list[index + 1]?.id;
    if (nextId) {
      await this.GET_SINGLE_POI(nextId);
      this.fetchNextPoi(nextId, list);
    }
  }

  async fetchPreviousPoi (currentId: PointOfInterest, list: PointOfInterest[]) {
    const index = list.findIndex((p: PointOfInterest) => p.id === currentId);
    const prevId = index > 0 && list[index - 1]?.id;
    if (prevId) {
      await this.GET_SINGLE_POI(prevId);
      this.fetchPreviousPoi(prevId, list);
    }
  }
  async fetchAroundPointOfInterest (
    currentId: PointOfInterest,
    list: PointOfInterest[]
  ) {
    if (list.length < 2) return;
    this.fetchNextPoi(currentId, list);
    this.fetchPreviousPoi(currentId, list);
  }

  async getMediaSyncData () {
    try {
      if (!this.pointOfInterest || !this.pointOfInterest.id)
        return (this.mediaSyncContent = null);
      const medaiSyncDataset = await this.GET_DATASET('mediaSync');
      if (!medaiSyncDataset || !medaiSyncDataset.value)
        return (this.mediaSyncContent = null);
      const mediaSyncData = medaiSyncDataset.value.find((media: any) => {
        // return media.id === 'video29'
        return media.pointOfInterestId === this.pointOfInterest?.id;
      });
      if (!mediaSyncData) return;
      // Mise en forme des content data
      const {
        medias,
        title,
        description,
        imageId,
        ...untranslatedData
      } = mediaSyncData;
      const image = imageId ? await this.GET_SINGLE_IMAGE(imageId) : null;
      const mediaI18n =
        medias.find((m: any) => m.language === this.$i18n.locale) ||
        medias.find((m: any) => m.language === 'fr') ||
        [];
      const audioId = mediaI18n?.audioId;
      const audio = audioId ? await this.GET_SINGLE_AUDIO(audioId) : null;
      if (audio) mediaI18n.audio = audio;
      this.mediaSyncContent = {
        __typename: 'MediaSyncContent',
        id: `media-sync-${mediaSyncData.id}`,
        data: {
          __typename: 'MediaSyncContentData',
          ...untranslatedData,
          image,
          i18n: {
            medias: mediaI18n,
            title:
              title.find((m: any) => m.language === this.$i18n.locale) ||
              title.find((m: any) => m.language === 'fr') ||
              [],
            description:
              description.find((m: any) => m.language === this.$i18n.locale) ||
              description.find((m: any) => m.language === 'fr') ||
              []
          }
        },
        languages: ['fr'],
        name: 'Media-Sync',
        options: { isAdditionalContent: false },
        people: [],
        shortCord: null,
        slug: null
      };
    } catch (error) {
      console.error(`Error during fetching media Sync data ${error}`);
    }
  }

  async init () {
    // On récupère le POI via son identifiant
    this.pointOfInterest = await this.GET_SINGLE_POI(this.$route.params.uuid);
    await this.setAudioguideCurrentPoi(this.pointOfInterest);
    await this.getMediaSyncData();

    this.sendMatomoInput();

    // Afin de créer les liens POI suivant et précédent nous devons connaître
    // le contexte lié à ce dernier (une collection, un parcours, aucun)
    if ('collection' in this.$route.query) {
      this.collection = await this.GET_SINGLE_COLLECTION(
        this.$route.query.collection as string,
        {
          take: 200
        }
      );
      if (this.collection?.id) {
        this.fetchAroundPointOfInterest(
          this.pointOfInterest.id,
          this.collection.pointsOfInterest.items
        );
      }
    }
    this.updateLocalStyle(
      this.pointOfInterest?.style || this.collection?.style
    );
    if (this.isBotEnableIsCurrentLanguage) this.sendContentToBot();
    // this.displayMapLink = await this.isPresentOnTheMap({type: 'pointOfInterest', uuid: this.pointOfInterest.id})
    this.checkAutoStartAudioguide();
  }

  get locks () {
    if (!this.pointOfInterest) return [];
    return this.pointOfInterest.locks;
  }

  get isLocked () {
    return !this.isRessourceAuhtorized(this.locks);
  }

  async activated () {
    await this.setAudioguideCurrentPoi(this.pointOfInterest);
    UI.EventBus.$emit(UI.ENABLE_DEFAULT_TOPBAR_TRANSPARENT);
    this.sendMatomoInput();
    this.checkAutoStartAudioguide();
    this.updateLocalStyle(
      this.pointOfInterest?.style || this.collection?.style
    );
    UI.EventBus.$on(UI.GO_TO_NEXT_POI, this.goToNextPoi);
  }

  beforeDestroy () {
    UI.EventBus.$off(UI.GO_TO_NEXT_POI, this.goToNextPoi);
  }

  goToNextPoi () {
    // idem que to="{ name: $route.name, params: { uuid: nextPointOfInterest.id }, query: $route.query }" 
    console.log('goToNextPoi');
    if (this.nextPointOfInterest) {
      this.$router.push({
        name: 'single-poi',
        params: { uuid: this.nextPointOfInterest.id },
        query: this.$route.query
      });
    }
  }

  sendMatomoInput () {
    if (
      !MATOMO_URL ||
      !this.pointOfInterest ||
      !this.pointOfInterest.i18n.title
    )
      return;
    const title = this.pointOfInterest.i18n.title.value.text as string;
    Matomo.default().trackPageView(title);
  }

  deactivated () {
    UI.EventBus.$emit(UI.DISABLE_DEFAULT_TOPBAR_TRANSPARENT);
    this.scrollPosY = 0;
    // this.cancelTTS()
    this.resetTts();
    this.checkAutoStopAudioguideWhenPoiIsClosed();
    this.updateLocalStyle(null);
  }

  get tabTitle () {
    return this.pointOfInterest && this.pointOfInterest.i18n.title
      ? this.pointOfInterest.i18n.title.value.text
      : this.$t('global.loading');
  }

  get pageTitle () {
    return this.pointOfInterest && this.pointOfInterest.i18n.title
      ? this.pointOfInterest.i18n.title.value.html
      : this.$t('global.loading');
  }

  get pointsOfInterestAround () {
    if (!this.collection) return null;
    const pointsOfInterest = this.collection.pointsOfInterest!.items!;
    const currentPoiIndex = pointsOfInterest.findIndex(
      (pointOfInterest: PointOfInterest) =>
        pointOfInterest.id === this.pointOfInterest!.id
    );
    return {
      prev: pointsOfInterest[currentPoiIndex - 1],
      next: pointsOfInterest[currentPoiIndex + 1]
    };
  }

  get prevPointOfInterest () {
    if (this.pointsOfInterestAround) return this.pointsOfInterestAround.prev;
  }

  get nextPointOfInterest () {
    if (this.pointsOfInterestAround) return this.pointsOfInterestAround.next;
  }

  get mediaInlineStyles () {
    return {
      transform: `translateY(-${this.scrollPosY / 5}px)`
    };
  }

  onViewScroll (e: any) {
    const mediaElem: HTMLElement = this.$refs.media as HTMLElement;
    if (mediaElem && e.target.scrollTop < mediaElem.offsetHeight) {
      this.scrollPosY = e.target.scrollTop;
      const percentage = (this.scrollPosY * 100) / mediaElem.offsetHeight;
      this.mediaScale = 1 + percentage / 500;
    }
  }

  checkAutoStartAudioguide () {
    console.log('checkAutoStartAudioguide', this.getAudioguidePoi, this.pointOfInterest);
    if (
      this.autoStopAudioGuideWhenDifferentPoiIsOpened &&
      this.getAudioguidePoi?.id !== this.pointOfInterest?.id
    )
      this.setAudioguideStatus(false);
    if (!this.playableAudioGuideTracks.length) return;
    if (!this.autoStartAudioGuideWhenPoiIsOpened) return;
    this.startAudioGuide();
  }

  checkAutoStopAudioguideWhenPoiIsClosed () {
    if (!this.autoStopAudioGuideWhenPoiIsClosed) return;
    this.setAudioguideStatus(false);
  }

  startAudioGuide () {
    this.setAudioguidePlaylist([...this.playableAudioGuideTracks]).then(() => {
      this.setAudioguidePoi(this.pointOfInterest);
      this.setAudioguideStatus(true);
    });
  }

  get playableAudioGuideTracks () {
    console.log('playableAudioGuideTracks', this.pointOfInterest);
    if (!this.pointOfInterest?.audioGuideTracks.length) return [];
    const enabledTranslation = (t: AudioGuideTrack) =>
      t.languages.includes(this.$i18n.locale);
    const hasMedia = (t: AudioGuideTrack) => t.i18n.audio || t.i18n.video;
    return this.pointOfInterest.audioGuideTracks
      .filter(enabledTranslation)
      .filter(hasMedia);
  }

  get showAudioGuideButton () {
    if (!this.pointOfInterest) return [];
    if (this.isLocked) return;
    console.log(
      'show audio guide button',
      this.playableAudioGuideTracks,
      this.$i18n.locale,
      this.pointOfInterest
    );
    return this.playableAudioGuideTracks.length;
  }

  datesString (bornAt: HtmlTranslation, diedAt: HtmlTranslation) {
    if (bornAt && diedAt) return `${bornAt.value.text} — ${diedAt.value.text}`
    if (bornAt && !diedAt) return `${bornAt.value.text}`
    if (!bornAt && diedAt) return ` * - ${diedAt.value.text}`
    if (!bornAt && !diedAt) return ``
  }
    
  showPerson (person: Person) {
    this.person = person;
    this.showPersonModal = true;
  }

  get isBotEnableIsCurrentLanguage () {
    if (this.isBotEnable && this.getCurrentLanguage) {
      const isBotEnable = this.isBotEnable[this.getCurrentLanguage];
      return !!isBotEnable;
    }
    return false;
  }

  sendContentToBot () {
    if (!this.pointOfInterest) return;
    const botContent = this.pointOfInterest.contents.filter(
      (content: any) => content.options.isAdditionalContent
    );
    if (!botContent || !botContent.length) return;
    this.addPoiContent({ ...this.pointOfInterest, contents: botContent });
  }

  get showReadAllPageButton () {
    return this.allowTts && !this.ttsIsStarted;
  }

  getIOSVersion () {
    const agent = window.navigator.userAgent;
    const start = agent.indexOf('OS');
    if (
      (agent.indexOf('iPhone') > -1 || agent.indexOf('iPad') > -1) &&
      start > -1
    ) {
      // @ts-ignore
      return window.Number(agent.substr(start + 3, 3).replace('_', '.'));
    }
    return 0;
  }

  ClickOnTTSButton () {
    this.readTtsList();
  }

  getBackupUrl (image: MediaImage) {
    if (!image.formats) return null;
    return this.smallerImageUrl(image);
  }

  get imageHeaderBackUpUrl () {
    if (!this.pointOfInterest?.image?.formats) return null;
    return this.smallerImageUrl(this.pointOfInterest.image);
  }

  get imageHeaderRouteQuery () {
    if (!this.pointOfInterest?.image) return {};
    const query = {
      context: 'poi',
      content_id: this.pointOfInterest.id
    };
    if (!this.imageHeaderBackUpUrl) return query;
    return { ...query, backUpUrl: this.imageHeaderBackUpUrl };
  }

  get getSingleImageHeaderRoute () {
    if (!this.pointOfInterest?.image) return null;
    return {
      name: 'single-media',
      params: {
        type: 'image',
        // uuid: this.pointOfInterest.image.originalImageId || this.pointOfInterest.image.id
        uuid: this.pointOfInterest.image.id
      },
      query: this.imageHeaderRouteQuery
    };
  }
}
